/**
 * MDM Command Catalog — single source of truth for every MDM action
 * scio3 can render and open-api can dispatch.
 *
 * Supersedes three drifted sources (to be deleted in Phase B):
 *   - apps/web/src/features/screens/utils/device-mdm.ts:DEVICE_MDM_COMMANDS
 *   - optisigns-open-api/src/app/mdm/mdm.inputs.ts:DEVICE_MDM_COMMANDS
 *   - packages/shared-types/src/mdm.ts  (already dead — zero consumers)
 *
 * Open-api keeps a parity copy at
 *   optisigns-open-api/src/app/device/mdm-commands-catalog.ts
 * CI diffs the two on every PR.
 *
 * This file is PURE DATA — no React, no Apollo, no side effects.
 * Predicates are small arrow functions over DeviceClassification.
 *
 * Translation provenance for each entry:
 *   predicate    ← bucket membership in frontend device-mdm.ts (14-bucket matrix)
 *   planFeatures ← COMMAND_PLAN_GATES in device-mdm.ts + getMdmButtonVisibility
 *                  top-level advancedDeviceManagement / remoteControl gates
 *   wireCommand  ← the enum KEY of EXECUTION_MUTATION_COMMANDS / MDM_MUTATION_COMMANDS in
 *                  open-api mdm.inputs.ts (GraphQL exposes KEYS; open-api maps key → stored
 *                  value). Commands that need a value open a dialog instead.
 *   isRetry / isCheckDuplicate / needsResponse
 *                ← per-command metadata in mdm.inputs.ts +
 *                  EXECUTION_MUTATION_COMMAND_NEED_RESPONSE
 *   confirmLevel / destructive
 *                ← MdmConfirmDialogs.tsx wiring + MdmButtonRow behavior
 *
 * Spec + rollout plan:
 *   docs/specs/19-optiedge-mdm/research/devices-compatibility-matrix-maintenance-best-practices.md §5
 */

import type { DeviceClassification, DeviceHardwareFamilyValue } from './device-classification';
import { DeviceHardwareFamily, isRoomSystem, isWebOsSignage } from './device-classification';

const H = DeviceHardwareFamily;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Typed union of every command id in the catalog.
 * Keep in sync with MDM_COMMANDS_CATALOG below — the CATALOG_BY_ID assertion
 * at the bottom of this file enforces it at compile time.
 */
export type MdmCommandId =
  // Quick actions (toolbar)
  | 'IDENTIFY_SCREEN'
  | 'MDM_REBOOT'
  | 'MDM_REFRESH'
  | 'MDM_CAPTURE_SCREENSHOT'
  | 'MDM_TOGGLE_REMOTE_CONTROL'
  | 'MDM_OPEN_REMOTE_VIEWER'
  // Dropdown / more commands
  | 'MDM_HDMI_CEC'
  | 'MDM_RS232'
  | 'MDM_GET_USED_STORAGE'
  | 'MDM_INSTALL_OPTISIGNS_VERSION'
  | 'MDM_DISABLE_AUTOUPDATE'
  | 'MDM_ENABLE_AUTOUPDATE'
  | 'MDM_INSTALL_APK'
  | 'MDM_OTA_UPDATE'
  | 'MDM_EXECUTE_REMOTE_COMMAND'
  // Child-dialog-bearing actions (read-only or input-gathering)
  | 'MDM_SET_TIMEZONE'
  | 'MDM_WIFI_MANAGEMENT'
  | 'MDM_VIEW_DEVICE_INFO'
  | 'MDM_COMMAND_HISTORY'
  // Platform-API actions (third-party room-system hardware — Zoom / Teams / Webex / Meet)
  | 'MDM_SYNC_NOW'
  | 'MDM_OPEN_EXTERNAL_ADMIN'
  | 'MDM_VIEW_ISSUES';

/**
 * Plan-feature flags on `company.feature` that gate MDM commands.
 * A command is hidden if ANY listed flag is false on the company doc.
 */
export type MdmPlanFeature =
  | 'advancedDeviceManagement'
  | 'remoteControl'
  | 'utilsMDM'
  | 'externalCom'
  /** OptiEdge MDM — gates platform-api commands (Zoom Rooms, Teams Rooms, etc).
   *  Ties to `company.feature.mdm` Boolean from Phase 1. */
  | 'mdm';

/**
 * How a command is dispatched when invoked.
 * Keeps wire commands, UI-surface openings, and feature toggles in one union
 * so the action row can iterate the catalog uniformly.
 */
export type MdmCommandAction =
  /** Sent via `addCommandExecution` (single-device, async heartbeat response). */
  | { type: 'execution'; wireCommand: string }
  /** Sent via `mdmCommand` (bulk-capable mutation). */
  | { type: 'mdm-mutation'; wireCommand: string }
  /** Toggles `device.feature.<key>` via `toggleFeature`. */
  | { type: 'feature-toggle'; featureKey: string }
  /** Opens a child dialog keyed by dialog id (see screen-dialog-store). */
  | { type: 'open-dialog'; dialogId: string }
  /** Fetches the remote-control link + launches the VNC viewer. */
  | { type: 'open-remote-viewer' }
  /** Flashes the screen via the dedicated `identifyScreen` mutation (a server-side content swap to a
   *  flash-asset template for ~30s — NOT `addCommandExecution`). Works on every device incl.
   *  webplayers with no on-device MDM agent. Distinct from the `screenIdentity` execution command,
   *  which is Video-Wall-only. Mirrors scio3 `mdm-command-presentation.ts`. */
  | { type: 'identify-screen' }
  /** Dispatches a platform-api operation (Zoom/Teams/Webex/Meet) through the
   *  open-api `device-platforms` service. `operation` is the adapter method
   *  name (e.g. 'syncNow', 'openAdminDeepLink', 'reboot'). */
  | { type: 'platform-api'; operation: string };

/**
 * Shape of a single catalog entry. Pure data.
 */
export interface MdmCommandDefinition {
  id: MdmCommandId;
  /** i18n key for the visible button label. Must exist in public/i18n/en.json. */
  i18nKey: string;
  /** Optional tooltip key. */
  tooltipKey?: string;
  /** Dispatch mechanism. */
  action: MdmCommandAction;
  /** Company-level plan-feature gates (AND-combined). */
  planFeatures: MdmPlanFeature[];
  /** Classification predicate (AND-combined with plan gates). */
  predicate: (c: DeviceClassification) => boolean;
  /** Confirmation style before dispatch. Default: 'none'. */
  confirmLevel?: 'none' | 'simple' | 'typed-name';
  /** User-visible destructive effect (Reboot, Unpair, …). */
  destructive?: boolean;
  /** Backend should retry on transient failure. */
  isRetry?: boolean;
  /** Backend should dedupe back-to-back identical commands. */
  isCheckDuplicate?: boolean;
  /** Command returns inline data in the mutation response. */
  needsResponse?: boolean;
  /** Minimum utils-mdm / player version. Not enforced in Phase A. */
  minPlayerVersion?: string;
}

// ---------------------------------------------------------------------------
// Predicate helpers
// ---------------------------------------------------------------------------

/** Predicate: hardware family is one of the listed values. */
const inFamily =
  (...families: DeviceHardwareFamilyValue[]) =>
  (c: DeviceClassification): boolean =>
    families.includes(c.hardwareFamily);

/** Predicate: the device has any management capability (utils-mdm OR builtin-mdm). */
const hasManagement = (c: DeviceClassification): boolean => c.managementCapability !== 'none';

// Note on Pro Max vs Pro Gen3: separate hardware families. Today's classifier
// cannot discriminate them from device fields (no discriminator ships over
// the wire yet), so a live ProPlayer Max is currently classified as
// OPTISIGNS_PRO_GEN3 OR OPTISIGNS_PRO_MAX based on whatever heuristic the
// classifier settles on. Both families appear in the predicate lists below
// so that when a discriminator lands (e.g. `feature.hwModel`), no predicate
// needs to change — only classifyDevice().

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export const MDM_COMMANDS_CATALOG: readonly MdmCommandDefinition[] = [
  // =========================================================================
  // Universal / identity
  // =========================================================================
  {
    id: 'IDENTIFY_SCREEN',
    i18nKey: 'Identify Screen',
    tooltipKey: 'Flash a pattern on the display so an on-site tech can spot it',
    // Dedicated identifyScreen mutation — `addCommandExecution('identifyScreen')` never worked
    // (not an EXECUTION_MUTATION_COMMANDS member). Matches scio3's presentation catalog.
    action: { type: 'identify-screen' },
    planFeatures: ['advancedDeviceManagement'],
    // In every bucket in today's DEVICE_MDM_COMMANDS — except platform-api
    // room systems (Zoom Rooms / Teams / Webex / Meet) which have no
    // vendor-side flash-pattern API.
    predicate: (c) => c.managementCapability !== 'platform-api',
  },

  // =========================================================================
  // Toolbar quick actions
  // =========================================================================
  {
    id: 'MDM_REBOOT',
    i18nKey: 'Reboot',
    tooltipKey: 'Reboot the device',
    action: { type: 'mdm-mutation', wireCommand: 'REBOOT' },
    planFeatures: ['advancedDeviceManagement'],
    confirmLevel: 'simple',
    destructive: true,
    // Scio2 buckets: OPTISIGNS_ANDROID, OPTISIGNS_PRO_MAX_AND_PRO_GEN3,
    //   OPTISIGNS_PRO_GEN2, OPTISIGNS_RPI, OPTISIGNS_WEBOS, OTHER_ELECTRONS,
    //   BUILTIN_MDM_ELECTRONS (Athena inherits BUILTIN_MDM_ELECTRONS).
    // Phase A note: ZOOM_ROOM intentionally excluded. The Zoom Rooms API has
    // no documented OS-reboot endpoint — only `zoomroom.restart` (app-level)
    // which lives under MDM_REFRESH. A "Reboot" button would lie about
    // capability. Teams / Webex / Meet likewise excluded.
    predicate: inFamily(
      H.OPTISIGNS_ANDROID,
      H.OPTISIGNS_PRO_MAX,
      H.OPTISIGNS_PRO_GEN3,
      H.OPTISIGNS_PRO_GEN2,
      H.RASPBERRY_PI,
      H.WEBOS_CONSUMER,
      H.WEBOS_SIGNAGE,
      H.WEBOS_SIGNAGE_ATHENA,
      H.OTHER_ELECTRON,
    ),
  },

  {
    id: 'MDM_REFRESH',
    i18nKey: 'Refresh & Relaunch',
    tooltipKey: 'Reload the player app without a full reboot',
    // wireCommand must be the GraphQL enum KEY (e.g. 'MDM_REFRESH'), not the
    // TypeScript enum value (`'refreshApp'`). NestJS `registerEnumType` exposes
    // enum KEYS as GraphQL members, so sending the lowercase value gets
    // rejected at GraphQL input validation. Backend internally maps key → value
    // when writing to command_executions and emitting over WebSocket, so the
    // device-side `appCommands['refreshApp']` lookup continues to work.
    action: { type: 'execution', wireCommand: 'MDM_REFRESH' },
    planFeatures: ['advancedDeviceManagement'],
    confirmLevel: 'simple',
    isRetry: true,
    isCheckDuplicate: true,
    // In every bucket EXCEPT OPTISIGNS_BRIGHT_SIGN + UNKNOWN. Phase A includes
    // H.ZOOM_ROOM — "Restart app" on a Zoom Room maps to zoomroom.restart via
    // PATCH /v2/rooms/{id}/events. Adapter translates the generic wire command
    // to the platform-specific call. Button label re-rendered per-platform in
    // MdmActionRow ("Restart app" for platform-api vs "Refresh & Relaunch"
    // elsewhere).
    predicate: (c) => c.hardwareFamily !== H.BRIGHTSIGN && c.hardwareFamily !== H.UNKNOWN,
  },

  {
    id: 'MDM_CAPTURE_SCREENSHOT',
    i18nKey: 'Take screenshot',
    action: { type: 'mdm-mutation', wireCommand: 'CAPTURE_SCREENSHOT' },
    planFeatures: ['advancedDeviceManagement'],
    confirmLevel: 'simple',
    // MdmButtonRow rule: isScreenshotAvailable = isAdvancedOptionAvailable || isDeviceWebOsSignage
    // Which normalizes to: managementCapability !== 'none'
    // (utils-mdm devices + builtin-mdm webos-signage can screenshot; matrix-only
    //  devices cannot). Platform-api room systems (Zoom / Teams / Webex / Meet)
    //  do not expose a screenshot API — Zoom cites privacy, others simply lack
    //  the capability — so they're excluded explicitly.
    predicate: (c) => hasManagement(c) && c.managementCapability !== 'platform-api',
  },

  {
    id: 'MDM_TOGGLE_REMOTE_CONTROL',
    i18nKey: 'Enable Remote Control',
    tooltipKey: 'Consent switch for the VNC viewer',
    action: { type: 'feature-toggle', featureKey: 'REMOTE_CONTROL' },
    planFeatures: ['remoteControl'],
    confirmLevel: 'simple',
    // scio2 rule: (electron + win32/linux) OR any device with utilsVersion.
    // Expressed against classification:
    predicate: (c) =>
      c.managementCapability === 'utils-mdm' ||
      c.hardwareFamily === H.WINDOWS_PC ||
      c.hardwareFamily === H.LINUX_GENERIC ||
      c.hardwareFamily === H.OTHER_ELECTRON,
  },

  {
    id: 'MDM_OPEN_REMOTE_VIEWER',
    i18nKey: 'Remote',
    tooltipKey: 'Remotely view and control the device',
    action: { type: 'open-remote-viewer' },
    planFeatures: ['remoteControl'],
    // Shown only when Remote Control is already enabled on the device.
    // The action row combines this predicate with device.feature.remoteControl
    // at render time (classification doesn't know per-device toggle state).
    predicate: (c) =>
      c.managementCapability === 'utils-mdm' ||
      c.hardwareFamily === H.WINDOWS_PC ||
      c.hardwareFamily === H.LINUX_GENERIC ||
      c.hardwareFamily === H.OTHER_ELECTRON,
  },

  // =========================================================================
  // External control
  // =========================================================================
  {
    id: 'MDM_HDMI_CEC',
    i18nKey: 'Send HDMI CEC',
    tooltipKey: 'Control connected display via HDMI CEC',
    // Needs a value (cecHDMIOn | cecHDMIOff) → dialog; it sends the enum KEY 'MDM_HDMI_CEC'.
    action: { type: 'open-dialog', dialogId: 'hdmiCec' },
    planFeatures: ['advancedDeviceManagement', 'externalCom'],
    isRetry: true,
    isCheckDuplicate: true,
    // Scio2 buckets: OPTISIGNS_ANDROID, OPTISIGNS_PRO_MAX_AND_PRO_GEN3,
    //   OPTISIGNS_WEBOS, OTHER_ELECTRONS, BUILTIN_MDM_ELECTRONS
    //   (Athena inherits BUILTIN_MDM_ELECTRONS).
    predicate: inFamily(
      H.OPTISIGNS_ANDROID,
      H.OPTISIGNS_PRO_MAX,
      H.OPTISIGNS_PRO_GEN3,
      H.WEBOS_CONSUMER,
      H.WEBOS_SIGNAGE,
      H.WEBOS_SIGNAGE_ATHENA,
      H.OTHER_ELECTRON,
    ),
  },

  {
    id: 'MDM_RS232',
    i18nKey: 'Send RS232',
    tooltipKey: 'Serial command to projector / panel',
    // Needs a port + payload → dialog; it sends the enum KEY 'MDM_RS232'.
    action: { type: 'open-dialog', dialogId: 'rs232Send' },
    planFeatures: ['advancedDeviceManagement', 'externalCom'],
    isRetry: true,
    needsResponse: true,
    // Buckets: OPTISIGNS_ANDROID, OPTISIGNS_PRO_MAX_AND_PRO_GEN3,
    //          OPTISIGNS_CHROME_OS, OPTISIGNS_WINDOWS, OTHER_ELECTRONS
    predicate: inFamily(
      H.OPTISIGNS_ANDROID,
      H.OPTISIGNS_PRO_MAX,
      H.OPTISIGNS_PRO_GEN3,
      H.CHROME_OS,
      H.WINDOWS_PC,
      H.OTHER_ELECTRON,
    ),
  },

  // =========================================================================
  // Storage / diagnostics
  // =========================================================================
  {
    id: 'MDM_GET_USED_STORAGE',
    i18nKey: 'Storage Details',
    tooltipKey: 'Used vs free bytes on device',
    // GraphQL enum KEY, not the value ('getUsedStorage') — same rule as MDM_REFRESH above; open-api
    // rejects the value at input validation. Matches scio3's presentation catalog.
    action: { type: 'execution', wireCommand: 'MDM_GET_USED_STORAGE' },
    planFeatures: ['advancedDeviceManagement', 'utilsMDM'],
    needsResponse: true,
    // Scio2 buckets: OPTISIGNS_ANDROID, OPTISIGNS_PRO_MAX_AND_PRO_GEN3,
    //   OPTISIGNS_WEBOS, OTHER_ANDROID_FIRESTICK, OTHER_ELECTRONS,
    //   BUILTIN_MDM_ELECTRONS (Athena inherits BUILTIN_MDM_ELECTRONS).
    predicate: inFamily(
      H.OPTISIGNS_ANDROID,
      H.OPTISIGNS_PRO_MAX,
      H.OPTISIGNS_PRO_GEN3,
      H.WEBOS_CONSUMER,
      H.WEBOS_SIGNAGE,
      H.WEBOS_SIGNAGE_ATHENA,
      H.AMAZON_FIRE_TV,
      H.OTHER_ELECTRON,
    ),
  },

  // =========================================================================
  // OTA / install
  // =========================================================================
  {
    id: 'MDM_INSTALL_OPTISIGNS_VERSION',
    i18nKey: 'Install OptiSigns Versions',
    tooltipKey: 'Pin or upgrade the player version',
    // Needs a version entry → dialog; it sends the enum KEY 'MDM_INSTALL_OPTISIGNS_VERSION'.
    action: { type: 'open-dialog', dialogId: 'installVersion' },
    planFeatures: ['advancedDeviceManagement', 'utilsMDM'],
    isRetry: true,
    isCheckDuplicate: true,
    // Buckets: OPTISIGNS_ANDROID, OPTISIGNS_PRO_MAX_AND_PRO_GEN3
    predicate: inFamily(H.OPTISIGNS_ANDROID, H.OPTISIGNS_PRO_MAX, H.OPTISIGNS_PRO_GEN3),
  },

  {
    id: 'MDM_OTA_UPDATE',
    i18nKey: 'OTA Update',
    tooltipKey: 'Apply over-the-air OS / firmware update',
    // 'otaUpdate' is not an execution command at all. OTA is a settings dialog (channel + window,
    // saved via updateDevice feature.ota*) plus a "check update" (executeRemoteCommand forceOTA),
    // so it opens the dialog — no bar-level confirm; the dialog confirms the check itself.
    action: { type: 'open-dialog', dialogId: 'otaUpdate' },
    planFeatures: ['advancedDeviceManagement', 'utilsMDM'],
    destructive: true,
    isRetry: true,
    isCheckDuplicate: true,
    // Bucket: OPTISIGNS_PRO_MAX_AND_PRO_GEN3 only
    predicate: inFamily(H.OPTISIGNS_PRO_MAX, H.OPTISIGNS_PRO_GEN3),
  },

  {
    id: 'MDM_DISABLE_AUTOUPDATE',
    i18nKey: 'Disable Auto Update',
    action: { type: 'execution', wireCommand: 'MDM_DISABLE_AUTOUPDATE' },
    planFeatures: ['advancedDeviceManagement', 'utilsMDM'],
    confirmLevel: 'simple',
    isRetry: true,
    isCheckDuplicate: true,
    // Bucket: OPTISIGNS_ANDROID only.
    // Visibility additionally requires device.feature.isDisableAutoUpdate to be
    // false/null/undefined (can't disable what's already disabled). The action
    // row evaluates that device-state gate at render time.
    predicate: inFamily(H.OPTISIGNS_ANDROID),
  },

  {
    id: 'MDM_ENABLE_AUTOUPDATE',
    i18nKey: 'Enable Auto Update',
    action: { type: 'execution', wireCommand: 'MDM_ENABLE_AUTOUPDATE' },
    planFeatures: ['advancedDeviceManagement', 'utilsMDM'],
    confirmLevel: 'simple',
    isRetry: true,
    isCheckDuplicate: true,
    // Mirror of DISABLE — same bucket, opposite device-state gate.
    predicate: inFamily(H.OPTISIGNS_ANDROID),
  },

  {
    id: 'MDM_INSTALL_APK',
    i18nKey: 'Install APK',
    tooltipKey: 'Push Android APK to the device',
    // Needs a URL → dialog; it sends the enum KEY 'MDM_INSTALL_APK'.
    action: { type: 'open-dialog', dialogId: 'installApk' },
    planFeatures: ['advancedDeviceManagement', 'utilsMDM'],
    isRetry: true,
    isCheckDuplicate: true,
    // Bucket: OPTISIGNS_ANDROID only
    predicate: inFamily(H.OPTISIGNS_ANDROID),
  },

  {
    id: 'MDM_EXECUTE_REMOTE_COMMAND',
    i18nKey: 'Execute Remote Commands',
    tooltipKey: 'Run an arbitrary shell command (staff / power user)',
    // Free-form command → dialog; it sends executeRemoteCommand (open-api maps a non-MDM command to
    // addCommandExecution MDM_EXECUTE_REMOTE_COMMAND itself).
    action: { type: 'open-dialog', dialogId: 'execute' },
    planFeatures: ['advancedDeviceManagement', 'utilsMDM'],
    destructive: true,
    isRetry: true,
    // Not in today's DEVICE_MDM_COMMANDS (it's gated separately by utilsMDM +
    // explicit permission). Visible on any utils-mdm device at the plan level.
    predicate: (c) => c.managementCapability === 'utils-mdm',
  },

  // =========================================================================
  // Child-dialog-bearing actions (input forms or read-only views)
  // =========================================================================
  {
    id: 'MDM_SET_TIMEZONE',
    i18nKey: 'Update time zone',
    tooltipKey: 'Pick IANA timezone to push to the device',
    action: { type: 'open-dialog', dialogId: 'timezone' },
    planFeatures: ['advancedDeviceManagement'],
    // Today's MdmButtonRow shows it whenever isShown — i.e. any management-capable
    // device except webos-signage. Webos-signage has timezone via its own MDM stack.
    // Platform-api room systems deferred to Phase B — Zoom room TZ is
    // calendar-driven and Zoom discourages overriding it (spec 07 §3.7).
    predicate: (c) =>
      hasManagement(c) && !isWebOsSignage(c) && c.managementCapability !== 'platform-api',
  },

  {
    id: 'MDM_WIFI_MANAGEMENT',
    i18nKey: 'Add/Remove WiFi',
    tooltipKey: 'Add or remove saved WiFi SSIDs',
    action: { type: 'open-dialog', dialogId: 'wifiManagement' },
    planFeatures: ['advancedDeviceManagement'],
    // Matches Timezone today — isShown gate, utils-mdm devices only.
    predicate: (c) => c.managementCapability === 'utils-mdm',
  },

  {
    id: 'MDM_VIEW_DEVICE_INFO',
    i18nKey: 'View device info',
    tooltipKey: 'System / hardware / network snapshot',
    action: { type: 'open-dialog', dialogId: 'mdmDeviceInfo' },
    planFeatures: ['advancedDeviceManagement'],
    predicate: (c) => hasManagement(c) && !isWebOsSignage(c),
  },

  {
    id: 'MDM_COMMAND_HISTORY',
    i18nKey: 'Remote command history',
    tooltipKey: 'Per-device audit of recent MDM commands',
    action: { type: 'open-dialog', dialogId: 'commandHistory' },
    planFeatures: ['advancedDeviceManagement'],
    // `hasManagement` is already true for platform-api families
    // (managementCapability === 'platform-api' !== 'none'), so room systems
    // pass the existing predicate. MDM_VIEW_DEVICE_INFO + MDM_COMMAND_HISTORY
    // surface command history / device info dialogs which are platform-agnostic.
    predicate: (c) => hasManagement(c) && !isWebOsSignage(c),
  },

  // =========================================================================
  // Platform-API commands — third-party room-system hardware
  //
  // New in Phase A (spec docs/specs/19-optiedge-mdm/zoom-rooms/07-zoom-rooms-integration-spec.md).
  // These commands dispatch through the open-api `device-platforms` service
  // and an adapter (Zoom Rooms in Phase A; Teams/Webex/Meet stubs in Phase B).
  // `planFeatures: ['mdm']` ties to `company.feature.mdm` Boolean — same plan
  // gate as Phase 1 Devices / Fleets pages.
  // =========================================================================
  {
    id: 'MDM_SYNC_NOW',
    i18nKey: 'Sync',
    tooltipKey: 'Pull latest state from the vendor',
    action: { type: 'platform-api', operation: 'syncNow' },
    planFeatures: ['mdm'],
    predicate: isRoomSystem,
  },

  {
    id: 'MDM_OPEN_EXTERNAL_ADMIN',
    // Label resolved per-platform at render time (e.g. "Open in Zoom Admin")
    // — MdmActionRow branches on classification.hardwareFamily to swap the
    // i18n key when the button is rendered. The key here is the default.
    i18nKey: 'Open in vendor admin',
    tooltipKey: 'Open this device in the vendor cloud console',
    action: { type: 'platform-api', operation: 'openAdminDeepLink' },
    planFeatures: ['mdm'],
    predicate: isRoomSystem,
  },

  {
    id: 'MDM_VIEW_ISSUES',
    i18nKey: 'View issues',
    tooltipKey: 'Camera, mic, network, and crash events',
    action: { type: 'open-dialog', dialogId: 'platformIssues' },
    planFeatures: ['mdm'],
    // Catalog predicate is permissive — any room-system device gets the
    // button. Real availability is gated by the adapter capability matrix
    // (`fetchIssues: true/false`). Currently true for Zoom Rooms (Dashboard
    // Issues API) and Teams Rooms (Microsoft Graph teamworkDevice/health);
    // false for Webex / Meet (Phase B, will flip once adapters wire up).
    // For platforms with capability=false the dialog opens, fires the
    // platformApiCommand → resolver returns BadRequest, dialog shows the
    // empty / error state — same UX as a successful empty result.
    predicate: isRoomSystem,
  },
];

// ---------------------------------------------------------------------------
// Accessors
// ---------------------------------------------------------------------------

/**
 * O(1) id → definition lookup. Built at module load from the catalog above.
 */
export const CATALOG_BY_ID: Record<MdmCommandId, MdmCommandDefinition> = (() => {
  const map = {} as Record<MdmCommandId, MdmCommandDefinition>;
  for (const cmd of MDM_COMMANDS_CATALOG) {
    map[cmd.id] = cmd;
  }
  return map;
})();

/**
 * Typed accessor.
 * Throws if the id is unknown — but the MdmCommandId union prevents that at
 * the type level, so callers should never hit the error in practice.
 */
export function getCommand(id: MdmCommandId): MdmCommandDefinition {
  const cmd = CATALOG_BY_ID[id];
  if (!cmd) {
    throw new Error(`Unknown MDM command id: ${id}`);
  }
  return cmd;
}

/**
 * Given a classification + the company's feature flags, return the ids of
 * catalog commands that should be shown.
 *
 * Used by:
 *   - open-api @ResolveField availableCommands
 *   - open-api MdmCapabilityGuard (validates incoming commands)
 *   - frontend MdmActionRow (renders allowed buttons) — in Phase A this reads
 *     the resolved field from GraphQL rather than calling this function directly
 *
 * When `reportedCapabilities` is provided (device self-reported via heartbeat),
 * the result is further narrowed to the intersection. Matrix is authoritative;
 * reported can only subtract, never add.
 */
export function resolveAvailableCommands(
  classification: DeviceClassification,
  companyFeatures: Partial<Record<MdmPlanFeature, boolean>>,
  reportedCapabilities?: readonly string[] | null,
): MdmCommandId[] {
  const allowed: MdmCommandId[] = [];
  for (const cmd of MDM_COMMANDS_CATALOG) {
    const planOk = cmd.planFeatures.every((f) => companyFeatures[f] === true);
    if (!planOk) continue;
    if (!cmd.predicate(classification)) continue;
    if (reportedCapabilities && !reportedCapabilities.includes(cmd.id)) continue;
    allowed.push(cmd.id);
  }
  return allowed;
}

// ---------------------------------------------------------------------------
// Compile-time exhaustiveness check
// ---------------------------------------------------------------------------
//
// If you add a new id to MdmCommandId but forget to add a row to
// MDM_COMMANDS_CATALOG, the following assertion fails at compile time.
// Keep it at the bottom so it runs after CATALOG_BY_ID is populated.

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _catalogExhaustive: Record<MdmCommandId, MdmCommandDefinition> = CATALOG_BY_ID;
